import { useMemo } from 'react'

import type { ButtonComputedOptions, ComputedResult } from './button.types'

import { useStyleConfig, useTheme } from '../core'
import { fontSizes } from '../theme'
import { activeClassName, cs, filterUndefined } from '../utils'
import { DEFAULT_BORDER_WIDTH, DEFAULT_BOX_SHADOW } from './consts'
import { extractNumericValue, resolveVariantKey } from './utils'

/**
 * Consolidated hook for computing all button styles and props.
 * Combines theme resolution, spacing, state-based styles, and className generation.
 * Reduces memoization overhead by computing all derived values in a single pass.
 *
 * @param options - See {@link ButtonComputedOptions} for individual field descriptions.
 * @returns See {@link ComputedResult}
 */
export const useButtonComputed = ({
  size,
  variant,
  intentProp,
  borderWidthProp,
  disabled,
  isActive,
  isIconOnly,
  isRound,
  isElevated,
  hasStartIcon,
  hasEndIcon,
  isFullWidth,
  className,
  variantProp,
}: ButtonComputedOptions): ComputedResult => {
  const theme = useTheme()
  const styles = useStyleConfig('Button', { size, ...variant }) || {}

  // Fallback to default variant — resolves correctly with the two-prop API.
  // resolveVariantKey with a non-falsy variant always returns a string; ?? 'primaryBrand' is unreachable.
  const fallbackVariant = resolveVariantKey('primary', intentProp ?? 'brand')!
  const defaultStyles = useStyleConfig('Button', { variant: fallbackVariant }) || {}

  return useMemo(() => {
    // Theme and style resolution — use default styles if container is empty or has no bg color
    const requestedContainer = styles.container || {}
    const hasValidStyles = requestedContainer.bg || requestedContainer.borderColor || requestedContainer.color
    const container = hasValidStyles ? requestedContainer : defaultStyles.container || {}
    const {
      borderWidth = DEFAULT_BORDER_WIDTH,
      paddingX = 16,
      paddingXIcon,
      paddingY = 12,
      gapX = 8,
      h = 40,
      fontSize = 'md',
      ...buttonStyles
    } = container

    // Normalize button styles with fallbacks for text colors only
    const normalizedStyles = {
      ...buttonStyles,
      activeColor: buttonStyles.activeColor ?? buttonStyles.color,
      hoverColor: buttonStyles.hoverColor ?? buttonStyles.color,
    }

    // Spacing calculations
    const border = extractNumericValue(borderWidthProp) || borderWidth || DEFAULT_BORDER_WIDTH
    const py = paddingY - border

    // Horizontal padding — two tiers (border handled by border-box, icon-only uses no padding):
    const pxForSide = (hasIcon: boolean) => {
      if (isIconOnly) return 0
      return hasIcon ? (paddingXIcon ?? paddingX) : paddingX
    }

    const spacing = {
      borderWidth: border,
      pl: `var(--vui-button-px-start, ${pxForSide(hasStartIcon)}px)`,
      pr: `var(--vui-button-px-end,   ${pxForSide(hasEndIcon)}px)`,
      pt: `var(--vui-button-py-start, ${isIconOnly ? 0 : py}px)`,
      pb: `var(--vui-button-py-end,   ${isIconOnly ? 0 : py}px)`,
      gap: `var(--vui-button-gap,     ${gapX}px)`,
    }

    // Disabled state props with CSS variable overrides
    // Theme tokens (like landGreen.70) are resolved by styled system, then wrapped in CSS variables
    // This allows both theme resolution and CSS variable overrides to work together
    const disabledProps = disabled
      ? filterUndefined({
          'aria-disabled': disabled,
          bg: container.disabledBg ?? undefined,
          borderColor: container.disabledBorderColor ?? undefined,
          color: container.disabledColor ?? undefined,
          hoverBg: container.disabledBg ?? normalizedStyles.bg,
          hoverBorderColor: container.disabledBorderColor ?? normalizedStyles.borderColor,
          hoverColor: container.disabledColor ?? normalizedStyles.color,
          activeBg: container.disabledBg ?? normalizedStyles.bg,
          cursor: 'not-allowed',
        })
      : {}

    // Active state props - prevents hover effects when button is active
    const activeProps = isActive
      ? filterUndefined({
          bg: container.activeBg,
          borderColor: container.activeBorderColor,
          color: container.activeColor,
          hoverBg: container.activeBg,
          hoverBorderColor: container.activeBorderColor,
          hoverColor: container.activeColor,
          cursor: 'default',
        })
      : {}

    // Aliased props (computed properties)
    const resolvedBoxShadow = theme.shadows[DEFAULT_BOX_SHADOW as keyof typeof theme.shadows] ?? DEFAULT_BOX_SHADOW
    const resolvedFontSize = fontSizes[fontSize as keyof typeof fontSizes] ?? fontSize
    const aliasedProps = filterUndefined({
      borderRadius: isRound
        ? `var(--vui-button-border-radius, 9999px)`
        : `var(--vui-button-border-radius, ${container.borderRadius || 0}px)`,
      boxShadow: isElevated ? `var(--vui-button-box-shadow, ${resolvedBoxShadow})` : undefined,
      fontSize: `var(--vui-button-font-size, ${resolvedFontSize}px)`,
      h: `var(--vui-button-h, ${h}px)`,
      minW: isFullWidth ? undefined : `var(--vui-button-min-w, auto)`,
      w: isIconOnly ? `var(--vui-button-w, ${h}px)` : isFullWidth ? '100%' : undefined,
    })

    // Class name generation
    const buttonClassName = cs(
      'vui-button',
      className,
      isActive && activeClassName,
      variantProp ? `vui-button-variant-${variantProp}` : undefined,
      intentProp ? `vui-button-intent-${intentProp}` : undefined,
    )

    return {
      buttonStyles: normalizedStyles,
      spacing,
      disabledProps,
      activeProps,
      aliasedProps,
      buttonClassName,
    }
  }, [
    styles,
    defaultStyles,
    theme,
    intentProp,
    borderWidthProp,
    hasStartIcon,
    hasEndIcon,
    isIconOnly,
    disabled,
    isActive,
    isRound,
    isElevated,
    isFullWidth,
    className,
    variantProp,
  ])
}
